import { Check, Circle, Eye, EyeOff, LoaderCircle } from "lucide-react";
import React from "react";
import { Button, Input, Label } from "./ui";

export const authInputClassName =
  "h-11 rounded-[0.625rem] border-slate-300 bg-white px-3 text-slate-950 shadow-none placeholder:text-slate-400 focus-visible:border-teal-700 focus-visible:ring-teal-600/15 disabled:bg-slate-50";

export function Field({
  label,
  error,
  children,
}: React.PropsWithChildren<{ label: string; error?: string }>) {
  return (
    <Label className="block text-sm font-medium text-slate-700">
      {label}
      <span className="mt-1.5 block">{children}</span>
      {error && (
        <span className="mt-1.5 block text-xs text-red-600">{error}</span>
      )}
    </Label>
  );
}

export function PasswordInput(
  props: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">
) {
  const [visible, setVisible] = React.useState(false);
  return (
    <span className="relative block">
      <Input
        {...props}
        type={visible ? "text" : "password"}
        className={`${authInputClassName} pr-11 ${props.className ?? ""}`}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute inset-y-0 right-0 h-full w-11 rounded-[0.625rem] text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus-visible:border-transparent focus-visible:ring-slate-200"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </Button>
    </span>
  );
}

export function SubmitButton({
  loading,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <Button
      {...props}
      type="submit"
      size="lg"
      disabled={loading || props.disabled}
      className={`h-11 w-full rounded-[0.625rem] bg-teal-700 font-semibold text-white hover:bg-teal-800 focus-visible:ring-teal-600/20 ${props.className ?? ""}`}
    >
      {loading && <LoaderCircle className="animate-spin" size={18} />}
      {children}
    </Button>
  );
}

const passwordChecks = [
  { label: "8 ou mais caracteres", test: (value: string) => value.length >= 8 },
  {
    label: "Uma letra maiúscula",
    test: (value: string) => /[A-Z]/.test(value),
  },
  {
    label: "Uma letra minúscula",
    test: (value: string) => /[a-z]/.test(value),
  },
  { label: "Um número", test: (value: string) => /\d/.test(value) },
  {
    label: "Um caractere especial",
    test: (value: string) => /[!@#$%^&*(),.?":{}|<>]/.test(value),
  },
] as const;

export function isStrongPassword(value: string) {
  return passwordChecks.every((check) => check.test(value));
}

export function PasswordRequirements({ password }: { password: string }) {
  return (
    <div className="rounded-xl bg-slate-50 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
        Sua senha precisa ter
      </p>
      <ul className="grid gap-1.5 text-xs sm:grid-cols-2">
        {passwordChecks.map((check) => {
          const valid = check.test(password);
          return (
            <li
              key={check.label}
              className={`flex items-center gap-2 ${
                valid ? "text-teal-700" : "text-slate-500"
              }`}
            >
              {valid ? <Check size={14} /> : <Circle size={12} />}
              {check.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function FormError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
    >
      {message}
    </div>
  );
}
